import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { urlParamReplicaId, urlProjectAreas } from '../../Constants';
import { IAction, IAppReduxStore, SlicePropsWithData } from '../store';
import { safeJsonParse } from '../../util/DataUtils';
import { apiFetch } from '../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectAreasState = {
    data: null,
    loading: false,
    error: null,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that fetches the available document view areas for a given project database.
     * @param replicaId - ReplicaId for the target project database.
     * @returns Promise for the HTTP request.
     */
    getProjectAreaConfig: async (replicaId: string): Promise<AreaDictionary> => {
        try {
            const url = urlProjectAreas.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url);
            const result = (await response.json()) as AreaDictionary;
            const mappedResult = Object.entries(result).reduce((acc, [field, value]) => {
                return {
                    ...acc,
                    [field]: {
                        ...value,
                        extendedConfig: safeJsonParse(value?.extendedConfig, null),
                    },
                };
            }, {} as AreaDictionary);
            return response.ok ? mappedResult : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectAreasState>) => {
        /**
         * Reducers for the 'getProjectAreaConfig' thunk.
         */

        builder.addCase(getProjectAreaConfig.pending, (state: Draft<IProjectAreasState>) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(
            getProjectAreaConfig.fulfilled,
            (state: Draft<IProjectAreasState>, action: IAction<AreaDictionary>) => {
                state.loading = false;
                state.error = null;
                state.data = action.payload;
            },
        );
        builder.addCase(
            getProjectAreaConfig.rejected,
            (state: Draft<IProjectAreasState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
                state.data = null;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const getProjectAreaConfig = createAsyncThunk('project/areas', thunks.getProjectAreaConfig);

/** Dictionary that contains configuration data on project areas. */
export type AreaDictionary = { [id: string]: IProjectAreaData };

/**
 * Interface that describes the state object for this redux slice.
 */
export type IProjectAreasState = SlicePropsWithData<AreaDictionary>;

/**
 * Interface that describes data returned from the backend for a project database view area.
 */
export interface IProjectAreaData {
    /** Area Name. */
    name: string;

    /** Dictionary of translations for the area's display name. */
    translations: { [id: string]: string };

    /** SVG Icon for the View Area. */
    icon: string;

    /** Display order of the View. */
    order: number;

    /** Additional config for Area. */
    extendedConfig?: string | null;
}

/**
 * Create the redux slice.
 */
export const projectAreasSlice = createSlice({
    name: 'projectAreas',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

export const getAreasExtendedConfigByField =
    (areaName: string, fieldName: string) =>
    (state: IAppReduxStore): string[] => {
        return state.projectAreas.data?.[areaName]?.extendedConfig?.[fieldName]?.split('|') ?? null;
    };

/**
 * Export slice reducer as default.
 */
export default projectAreasSlice.reducer;
